import { useState } from "react";
import { AsyncPaginate } from "react-select-async-paginate";

import { useTheme } from "../../constants/ThemeProvider";

const LocationPicker = ({ value = [], onChange }) => {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const [selected, setSelected] = useState(
    value.map((loc) => ({ value: loc, label: loc }))
  );

  const loadOptions = async (search) => {
    if (!search) return { options: [] };

    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
        search
      )}`
    );
    const data = await res.json();

    return {
      options: data.map((place) => ({
        value: place.place_id,       // unique id to avoid duplicates
        label: place.display_name,   // user-facing string
        raw: place,                  // keep raw data if needed later
      })),
    };
  };

  const handleChange = (newValue) => {
    setSelected(newValue);
    // Save display_name strings into preferences (not place_id)
    onChange(newValue.map((v) => v.label));
  };

  return (
    <AsyncPaginate
      isMulti
      value={selected}
      loadOptions={loadOptions}
      onChange={handleChange}
      placeholder="Search cities or countries..."
      styles={{
        control: (base, state) => ({
          ...base,
          padding: "0.25rem",
          backgroundColor: isDark ? "#1f2937" : "#ffffff",
          border: `1px solid ${isDark ? "#4B5563" : "#D1D5DB"}`,
          borderRadius: "0.5rem",
          fontSize: "0.875rem",
          outline: "none",
          boxShadow: "none",
          "&:hover": { borderColor: "#3B82F6" },
          borderColor: state.isFocused ? "#3B82F6" : isDark ? "#4B5563" : "#D1D5DB",
        }),
        input: (base) => ({
          ...base,
          color: isDark ? "#F3F4F6" : "#111827",
        }),
        singleValue: (base) => ({
          ...base,
          color: isDark ? "#F3F4F6" : "#111827",
        }),
        placeholder: (base) => ({
          ...base,
          color: isDark ? "#9CA3AF" : "#9CA3AF",
        }),
        menu: (base) => ({
          ...base,
          backgroundColor: isDark ? "#1f2937" : "#ffffff",
          border: `1px solid ${isDark ? "#4B5563" : "#D1D5DB"}`,
          borderRadius: "0.5rem",
          marginTop: "0.25rem",
          boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
        }),
        option: (base, state) => ({
          ...base,
          backgroundColor: state.isFocused
            ? isDark
              ? "#374151"
              : "#EFF6FF"
            : "transparent",
          color: isDark ? "#F3F4F6" : "#111827",
        }),
        multiValue: (base) => ({
          ...base,
          display: "flex",
          alignItems: "center",
          gap: "0.25rem",
          backgroundColor: isDark ? "#1e3a5f" : "#DBEAFE",
          color: isDark ? "#93C5FD" : "#1D4ED8",
          borderRadius: "9999px",
          padding: "0.25rem 0.4rem",
          fontSize: "0.975rem",
          fontWeight: 500,
        }),
        multiValueLabel: (base) => ({
          ...base,
          color: isDark ? "#93C5FD" : "#1D4ED8",
          padding: 0,
        }),
        multiValueRemove: (base) => ({
          ...base,
          color: isDark ? "#9CA3AF" : "#6B7280",
          cursor: "pointer",
          ":hover": {
            color: isDark ? "#E5E7EB" : "#374151",
            backgroundColor: "transparent",
          },
        }),
      }}
    />
  );
};

export default LocationPicker;
